<template>
  <div class="page-container">
    <div class="settings-form">
      <h2 class="page-title">邮件模板管理</h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.keyword" placeholder="请输入模板名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>
          搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>
          新增模板
        </el-button>
      </div>

      <!-- 邮件模板列表 -->
      <el-table :data="templateList" stripe style="width: 100%; margin-top: 20px" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column label="关联用户" width="150">
          <template #default="{ row }">
            <div class="user-info">
              <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
              <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="template_name" label="模板名称" width="150" />
        <el-table-column prop="subject" label="邮件主题" width="200" />
        <el-table-column prop="content" label="邮件内容">
          <template #default="{ row }">
            <div class="content-preview">{{ row.content }}</div>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="80" align="center">
          <template #default="{ row }">
            <el-tag :type="row.status === 1 ? 'success' : 'danger'">
              {{ row.status === 1 ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="操作" width="280" fixed="right">
          <template #default="scope">
            <el-button type="info" size="small" @click="openTestDialog(scope.row)">
              <el-icon>
                <Promotion />
              </el-icon>
              测试
            </el-button>
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>
              编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchTemplateList" @current-change="fetchTemplateList" />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="800px" :close-on-click-modal="false">
      <el-form ref="templateForm" :model="templateForm" label-width="120px" :rules="formRules">
        <el-form-item label="模板名称" prop="template_name">
          <el-input v-model="templateForm.template_name" placeholder="请输入模板名称" size="large" />
        </el-form-item>

        <el-form-item label="邮件主题" prop="subject">
          <el-input v-model="templateForm.subject" placeholder="请输入邮件主题" size="large" />
          <div class="form-tip">可使用变量：{name} - 收件人名称</div>
        </el-form-item>

        <el-form-item label="邮件内容" prop="content">
          <el-input v-model="templateForm.content" placeholder="请输入邮件内容" type="textarea" :rows="8" />
          <div class="form-tip">可使用变量：{name} - 收件人名称</div>
        </el-form-item>

        <el-form-item label="状态">
          <el-radio-group v-model="templateForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">禁用</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="备注信息">
          <el-input v-model="templateForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTemplateForm">确认</el-button>
      </template>
    </el-dialog>

    <!-- 测试对话框 -->
    <el-dialog v-model="testDialogVisible" title="测试邮件模板" width="500px" :close-on-click-modal="false">
      <el-form ref="testForm" :model="testForm" label-width="100px">
        <el-form-item label="邮箱账户" prop="account_id">
          <el-select v-model="testForm.account_id" placeholder="请选择邮箱账户" style="width: 100%" clearable>
            <el-option v-for="account in accountList" :key="account.id" :label="account.account_name"
              :value="account.id" />
          </el-select>
        </el-form-item>

        <el-form-item label="收件人邮箱" prop="receiver_email">
          <el-input v-model="testForm.receiver_email" placeholder="请输入收件人邮箱地址" size="large" />
        </el-form-item>

        <el-form-item label="收件人名称">
          <el-input v-model="testForm.receiver_name" placeholder="请输入收件人名称（可选）" size="large" />
        </el-form-item>

        <el-form-item>
          <el-alert type="info" show-icon :closable="false">
            <template #title>
              <div>如果填写了收件人名称，邮件中将自动替换 {name} 变量</div>
            </template>
          </el-alert>
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="testDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitTest" :loading="testing">发送测试邮件</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Promotion
} from '@element-plus/icons-vue'

export default {
  name: 'EmailTemplate',
  components: { Search, Plus, Edit, Delete, Promotion },
  data() {
    return {
      loading: false,
      testing: false,
      dialogVisible: false,
      testDialogVisible: false,
      dialogTitle: '新增邮件模板',
      isEditMode: false,
      currentTemplateId: null,
      accountList: [],
      searchForm: {
        keyword: ''
      },
      templateForm: {
        id: null,
        template_name: '',
        subject: '',
        content: '',
        status: 1,
        notes: ''
      },
      testForm: {
        account_id: null,
        receiver_email: '',
        receiver_name: ''
      },
      formRules: {
        template_name: [
          { required: true, message: '请输入模板名称', trigger: 'blur' }
        ],
        subject: [
          { required: true, message: '请输入邮件主题', trigger: 'blur' }
        ],
        content: [
          { required: true, message: '请输入邮件内容', trigger: 'blur' }
        ],
        account_id: [
          { required: true, message: '请选择邮箱账户', trigger: 'change' }
        ],
        receiver_email: [
          { required: true, message: '请输入收件人邮箱', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: 'blur' }
        ]
      },
      templateList: [],
      userList: [], // 用户列表
      userMap: {},  // 用户ID映射
      userLoading: false,
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      }
    }
  },
  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}')
    }
  },
  mounted() {
    this.fetchTemplateList();
    this.fetchAccountList();
    this.fetchUserList();
  },
  methods: {
    // 获取账户列表
    async fetchAccountList() {
      try {
        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/email_account.php`,
          {
            action: 'list',
            store_id: this.userInfo.id,
            page: 1,
            page_size: 1000
          }
        );

        if (data.data) {
          this.accountList = data.data.filter(account => account.status === 1);
        }
      } catch (error) {
        console.error('获取邮箱账户列表错误:', error);
      }
    },

    // 获取用户列表
    async fetchUserList() {
      this.userLoading = true
      try {
        const response = await axios.post(
          `${config.BASE_URL}/shop/api/business_card_management.php`,
          {
            action: 'get_users',
            store_id: this.userInfo.id
          }
        )

        if (response.data.data) {
          this.userList = response.data.data
          // 构建用户映射
          this.userMap = {}
          response.data.data.forEach(user => {
            this.userMap[user.id] = user
          })
        }
      } catch (error) {
        console.error('获取用户列表错误:', error)
      } finally {
        this.userLoading = false
      }
    },

    // 根据user_id获取用户信息
    getUserInfo(userId) {
      const user = this.userMap[userId]
      if (user) {
        return {
          nickname: user.nickname,
          phone: user.phone,
          avatar: user.avatar
        }
      }
      return {
        nickname: '管理员',
        phone: '',
        avatar: ''
      }
    },
    // 获取模板列表
    async fetchTemplateList() {
      this.loading = true;
      try {
        const params = {
          action: 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };

        const { data } = await axios.post(
          `${config.BASE_URL}/shop/api/email_template.php`,
          params
        );

        if (data.data) {
          this.templateList = data.data;
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        this.$message.error('获取邮件模板列表失败');
        console.error('获取邮件模板列表错误:', error);
      } finally {
        this.loading = false;
      }
    },

    // 处理搜索
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTemplateList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增邮件模板';
      this.isEditMode = false;
      this.templateForm = {
        id: null,
        template_name: '',
        subject: '',
        content: '',
        status: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑邮件模板';
      this.isEditMode = true;
      this.templateForm = {
        id: row.id,
        template_name: row.template_name,
        subject: row.subject,
        content: row.content,
        status: row.status,
        notes: row.notes
      };
      this.dialogVisible = true;
    },

    // 打开测试对话框
    openTestDialog(row) {
      this.currentTemplateId = row.id;
      this.testForm = {
        account_id: null,
        receiver_email: '',
        receiver_name: ''
      };
      this.testDialogVisible = true;
    },

    // 提交表单（新增/编辑）
    submitTemplateForm() {
      this.$refs.templateForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/email_template.php`,
              {
                action,
                ...this.templateForm,
                store_id: this.userInfo.id
              }
            );

            if (data.success) {
              this.$message.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTemplateList();
            } else {
              this.$message.error(data.error || '操作失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '操作失败';
            this.$message.error(`操作失败: ${errorMsg}`);
            console.error('表单提交错误:', error);
          }
        }
      });
    },

    // 提交测试
    submitTest() {
      this.$refs.testForm.validate(async (valid) => {
        if (valid) {
          this.testing = true;
          try {
            const { data } = await axios.post(
              `${config.BASE_URL}/shop/api/email_template.php`,
              {
                action: 'test',
                id: this.currentTemplateId,
                store_id: this.userInfo.id,
                ...this.testForm
              }
            );

            if (data.success) {
              this.$message.success('模板测试邮件发送成功');
              this.testDialogVisible = false;
            } else {
              this.$message.error(data.error || '测试失败');
            }
          } catch (error) {
            const errorMsg = error.response?.data?.error || error.message || '测试失败';
            this.$message.error(`测试失败: ${errorMsg}`);
            console.error('测试错误:', error);
          } finally {
            this.testing = false;
          }
        }
      });
    },

    // 删除模板
    async handleDelete(id) {
      this.$confirm('确定要删除该邮件模板吗？', '警告', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/email_template.php`,
            {
              action: 'delete',
              id: id,
              store_id: this.userInfo.id
            }
          );

          if (data.success) {
            this.$message.success('删除成功');
            this.fetchTemplateList();
          } else {
            this.$message.error(data.error || '删除失败');
          }
        } catch (error) {
          const errorMsg = error.response?.data?.error || error.message || '删除失败';
          this.$message.error(`删除失败: ${errorMsg}`);
          console.error('删除错误:', error);
        }
      }).catch(() => { });
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 0px;
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.content-preview {
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}

/* 表格样式优化 */
.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

/* 操作按钮样式 */
.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

/* 对话框样式优化 */
:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;
    margin-right: 0;

    .el-dialog__title {
      font-weight: 600;
      color: #1f2937;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

/* 表单验证提示样式 */
:deep(.el-form-item__error) {
  font-size: 12px;
  padding-top: 4px;
}

.user-info {
  .user-nickname {
    font-weight: 500;
    margin-bottom: 4px;
  }

  .user-phone {
    color: #909399;
    font-size: 12px;
  }
}
</style>